import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Loader2, X } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { diagnosticQuestionsScale, diagnosticQuestionsReflection } from '../mockData';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiagnosticoFinanciero = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('intro'); // intro, contact, section1, section2, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [leadId, setLeadId] = useState(null);
  const [scaleAnswers, setScaleAnswers] = useState({});
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [totalScore, setTotalScore] = useState(0);

  const handleStartDiagnostic = () => {
    setCurrentPage('contact');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Por favor completa tu nombre y email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      });
      
      setLeadId(response.data.id);
      setCurrentPage('section1');
      setCurrentQuestion(0);
      toast.success('¡Comencemos con tu diagnóstico!');
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Error al guardar tu información. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleScaleSelect = (questionId, value) => {
    setScaleAnswers({ ...scaleAnswers, [questionId]: value });
  };

  const handleReflectionChange = (questionId, value) => {
    setReflectionAnswers({ ...reflectionAnswers, [questionId]: value });
  };

  const handleNextSection1 = () => {
    const currentQ = diagnosticQuestionsScale[currentQuestion];
    if (scaleAnswers[currentQ.id] === undefined) {
      toast.error('Por favor selecciona un valor');
      return;
    }

    if (currentQuestion < diagnosticQuestionsScale.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate total score for section 1
      const score = Object.values(scaleAnswers).reduce((sum, val) => sum + parseInt(val), 0);
      setTotalScore(score);
      setCurrentPage('section2');
      setCurrentQuestion(0);
    }
  };

  const handleNextSection2 = () => {
    const currentQ = diagnosticQuestionsReflection[currentQuestion];
    if (!reflectionAnswers[currentQ.id] || reflectionAnswers[currentQ.id].trim() === '') {
      toast.error('Por favor escribe tu reflexión');
      return;
    }

    if (currentQuestion < diagnosticQuestionsReflection.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentPage === 'section2') {
      setCurrentPage('section1');
      setCurrentQuestion(diagnosticQuestionsScale.length - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Prepare answers array
      const answersArray = [
        ...Object.keys(scaleAnswers).map(questionId => {
          const question = diagnosticQuestionsScale.find(q => q.id === parseInt(questionId));
          return {
            question_id: parseInt(questionId),
            question: question.question,
            answer: `${scaleAnswers[questionId]}/10`,
            type: 'scale'
          };
        }),
        ...Object.keys(reflectionAnswers).map(questionId => {
          const question = diagnosticQuestionsReflection.find(q => q.id === parseInt(questionId));
          return {
            question_id: parseInt(questionId),
            question: question.question,
            answer: reflectionAnswers[questionId],
            type: 'text'
          };
        })
      ];

      // Submit diagnostic responses
      await axios.post(`${API}/diagnostic-responses`, {
        lead_id: leadId,
        answers: answersArray,
        total_score: totalScore
      });

      setCurrentPage('results');
      toast.success('¡Diagnóstico completado exitosamente!');
    } catch (error) {
      console.error('Error submitting diagnostic:', error);
      toast.error('Error al guardar tu diagnóstico. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreInterpretation = (score) => {
    if (score <= 30) return { level: 'Rojo', message: 'Nada / Cero claridad o control' };
    if (score <= 60) return { level: 'Amarillo', message: 'A veces / Parcialmente' };
    return { level: 'Verde', message: 'Casi siempre o Siempre / Claridad y Control' };
  };

  // Intro Page
  if (currentPage === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
        <Header />
        <div className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <h1 className="text-4xl sm:text-5xl font-serif font-light text-charcoal mb-6 leading-tight">
                  Es momento de recuperar el control de tu dinero y de tu futuro
                </h1>
              </div>

              <div className="prose max-w-none mb-12">
                <p className="text-lg text-charcoal/80 leading-relaxed mb-6">
                  La situación financiera que tienes hoy no es casualidad… es solo un <strong>RESULTADO</strong>. 
                  Un reflejo directamente proporcional, de cómo has pensado, sentido y actuado con respecto al dinero.
                </p>
                
                <p className="text-lg text-charcoal/80 leading-relaxed mb-6">
                  Pero aquí está lo más importante: <strong>Ese resultado puede cambiar</strong>.
                </p>
                
                <p className="text-lg text-charcoal/80 leading-relaxed mb-8">
                  Cuando transformas tus creencias, tus hábitos y tus patrones financieros, cambias el rumbo completo 
                  de tu vida. Y cuando haces ese cambio de raíz… la paz financiera e incluso la libertad financiera 
                  deja de ser un sueño, y comienza a convertirse en tu nueva realidad.
                </p>

                <div className="bg-golden/10 rounded-lg p-6 mb-8">
                  <h3 className="text-2xl font-serif text-charcoal mb-4">
                    🔥 SUMÉRGETE EN TUS FINANZAS DESDE LA RAÍZ 🔥
                  </h3>
                  <p className="text-charcoal/70">
                    Vamos más profundo que la superficie.
                  </p>
                </div>

                <Button
                  onClick={handleStartDiagnostic}
                  className="w-full bg-golden hover:bg-golden-dark text-white py-6 text-lg rounded-lg"
                >
                  Comenzar mi Diagnóstico Financiero
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Contact Form Page
  if (currentPage === 'contact') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
        <Header />
        <div className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-2xl">
            <Card className="p-8 sm:p-12">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <h3 className="text-2xl font-serif text-charcoal mb-6">
                    Comencemos con tu información de contacto
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-cream-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-golden"
                    placeholder="Tu nombre"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-cream-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-golden"
                    placeholder="tu@email.com"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    Teléfono (opcional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-cream-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-golden"
                    placeholder="+52 555 123 4567"
                    disabled={isLoading}
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-golden hover:bg-golden-dark text-white py-4 rounded-lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Guardando...
                    </>
                  ) : (
                    <>
                      Comenzar Diagnóstico
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Section 1: Scale Questions (0-10)
  if (currentPage === 'section1') {
    const currentQ = diagnosticQuestionsScale[currentQuestion];
    const progress = ((currentQuestion + 1) / diagnosticQuestionsScale.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
        <Header />
        <div className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-golden/10 text-golden text-sm font-medium tracking-wider rounded-full mb-4">
                🧠 CONCIENCIA DE TUS HÁBITOS FINANCIEROS
              </span>
              <h2 className="text-2xl font-serif text-charcoal mb-4">
                🧠 SEMÁFORO FINANCIERO
              </h2>
              <p className="text-charcoal/70">
                Responde del 0 al 10. Donde 0-3 = Nunca/Nada, 4-6 = A veces, 7-10 = Siempre/Control total
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-charcoal/60 mb-2">
                <span>Pregunta {currentQuestion + 1} de {diagnosticQuestionsScale.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-cream-dark rounded-full overflow-hidden">
                <div 
                  className="h-full bg-golden transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <Card className="p-8 sm:p-12">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-serif text-charcoal mb-8">
                    {currentQuestion + 1}. {currentQ.question}
                  </h3>
                </div>

                {/* Scale Selector 0-10 */}
                <div className="space-y-4">
                  <div className="grid grid-cols-11 gap-2">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                      <button
                        key={value}
                        onClick={() => handleScaleSelect(currentQ.id, value)}
                        className={`aspect-square rounded-lg border-2 font-medium transition-all duration-200 ${
                          scaleAnswers[currentQ.id] === value
                            ? 'border-golden bg-golden text-white scale-110'
                            : 'border-cream-dark hover:border-golden/50 text-charcoal'
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-charcoal/60 px-1">
                    <span>0 - Nunca/Nada</span>
                    <span>5 - A veces</span>
                    <span>10 - Siempre</span>
                  </div>
                </div>

                <div className="flex justify-between pt-6">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="border-2 border-cream-dark"
                    disabled={currentQuestion === 0}
                  >
                    <ArrowLeft className="mr-2" size={20} />
                    Anterior
                  </Button>

                  <Button
                    onClick={handleNextSection1}
                    className="bg-golden hover:bg-golden-dark text-white"
                  >
                    {currentQuestion === diagnosticQuestionsScale.length - 1 ? 'Siguiente Sección' : 'Siguiente'}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Section 2: Reflection Questions
  if (currentPage === 'section2') {
    const currentQ = diagnosticQuestionsReflection[currentQuestion];
    const progress = ((currentQuestion + 1) / diagnosticQuestionsReflection.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
        <Header />
        <div className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-golden/10 text-golden text-sm font-medium tracking-wider rounded-full mb-4">
                🧠 CONCIENCIA DE TUS PATRONES FINANCIEROS
              </span>
              <h2 className="text-2xl font-serif text-charcoal mb-4">
                Reflexiona sobre tu relación con el dinero
              </h2>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-charcoal/60 mb-2">
                <span>Pregunta {currentQuestion + 1} de {diagnosticQuestionsReflection.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-cream-dark rounded-full overflow-hidden">
                <div 
                  className="h-full bg-golden transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <Card className="p-8 sm:p-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-serif text-charcoal mb-6">
                    {currentQuestion + 1}. {currentQ.question}
                  </h3>
                </div>

                <div>
                  <textarea
                    value={reflectionAnswers[currentQ.id] || ''}
                    onChange={(e) => handleReflectionChange(currentQ.id, e.target.value)}
                    className="w-full px-4 py-3 border border-cream-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-golden min-h-32 resize-y"
                    placeholder="Escribe tu reflexión aquí..."
                    disabled={isLoading}
                  />
                </div>

                <div className="flex justify-between pt-6">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="border-2 border-cream-dark"
                    disabled={isLoading}
                  >
                    <ArrowLeft className="mr-2" size={20} />
                    Anterior
                  </Button>

                  <Button
                    onClick={handleNextSection2}
                    className="bg-golden hover:bg-golden-dark text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Guardando...
                      </>
                    ) : (
                      <>
                        {currentQuestion === diagnosticQuestionsReflection.length - 1 ? 'Finalizar' : 'Siguiente'}
                        <ArrowRight className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Results Page
  if (currentPage === 'results') {
    const scoreInterpretation = getScoreInterpretation(totalScore);

    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
        <Header />
        <div className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="p-8 sm:p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-serif text-charcoal mb-4">
                ¡Felicidades ✨✨✨!
              </h2>
              
              <p className="text-lg text-charcoal/70 mb-2">
                Ya estás consciente de tu situación financiera al día de hoy, <strong>{formData.name}</strong>.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 text-sm">
                  📧 <strong>¡Email enviado!</strong> Hemos enviado un resumen completo de tu diagnóstico a <strong>{formData.email}</strong> 
                  incluyendo un PDF con todos los detalles y recomendaciones personalizadas.
                </p>
              </div>

              <div className="bg-golden/10 rounded-lg p-6 mb-8">
                <h3 className="text-2xl font-serif text-charcoal mb-2">
                  Tu Puntaje: {totalScore}/100
                </h3>
                <div className={`inline-block px-4 py-2 rounded-full ${
                  scoreInterpretation.level === 'Verde' ? 'bg-green-100 text-green-700' :
                  scoreInterpretation.level === 'Amarillo' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  Nivel: {scoreInterpretation.level} - {scoreInterpretation.message}
                </div>
              </div>

              <div className="prose max-w-none mb-8 text-left">
                <h3 className="text-2xl font-serif text-charcoal mb-4 text-center">
                  ¿Ahora qué debo hacer?
                </h3>
                
                <p className="text-lg text-charcoal/80 mb-4">
                  Ahora estás lista para dar el paso al cambio.
                </p>
                
                <p className="text-lg text-charcoal/80 mb-6">
                  Te voy a enseñar cómo pasar de cero hasta tu primera inversión en México y en Estados Unidos 
                  en solo 3 meses.
                </p>

                <div className="bg-charcoal text-white rounded-lg p-8 mb-8">
                  <h4 className="text-2xl font-serif mb-4">Tu futuro económico está en tus manos</h4>
                  <p className="text-lg mb-4">
                    ¿Estás lista para transformar tus finanzas, aprender estrategias de ahorro e inversión, 
                    y tener más dinero en tu cuenta bancaria sin depender económicamente de nadie?
                  </p>
                  <p className="text-golden font-medium text-xl mb-4">
                    🚀 Tú decides…
                  </p>
                  <p className="mb-2">
                    ¿Sigues igual o das el paso hacia la paz y libertad financiera que mereces?
                  </p>
                </div>
              </div>

              <Button 
                onClick={() => window.open('https://hotmart.com/es/club/transformacion-financiera-t', '_blank')}
                className="bg-golden hover:bg-golden-dark text-white px-8 py-6 text-lg rounded-lg mb-6"
              >
                Comienza tu Transformación Financiera
                <ArrowRight className="ml-2" size={24} />
              </Button>

              <button
                onClick={() => navigate('/')}
                className="text-charcoal/70 hover:text-golden transition-colors"
              >
                Volver al inicio
              </button>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return null;
};

export default DiagnosticoFinanciero;